/**
 * Flight area helpers — the single mission geometry (mission-map unification, PR6).
 *
 * A mission has exactly one flight area: a Polygon without holes, a LineString flown inside a
 * corridor of `corridor_width_m` (total width, half on each side of the centreline), or a circle — a
 * Point (the centre) with `circle_radius_m`. The server is always the authority: limits (corridor
 * width and circle radius range/default, take-off distance tolerance, import size)
 * come from GET /missions/flight-area/settings and every check runs again in
 * POST /missions/flight-area/validate and on the mission save itself. Nothing here duplicates those
 * limits — the helpers below only shape data for the map, the panel and the payload.
 */
import { useEffect, useRef, useState } from 'react';
import api from './api';

// Formats POST /missions accepts in flight_area_source.format (GeospatialFormats.ImportFormats on the
// server — an unknown value would fail the whole save with flight_area_source_invalid).
export const FLIGHT_AREA_SOURCE_FORMATS = ['kml', 'kmz', 'geojson', 'gpx', 'csv'];

export const FLIGHT_AREA_KIND = {
  NONE: 'none',
  POLYGON: 'polygon',
  LINE: 'line',
  CIRCLE: 'circle',
  INVALID: 'invalid',
};

const isPosition = (p) =>
  Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]);

/**
 * Classifies a stored/drawn flight_area. `reason` (only for INVALID) is a short machine reason the UI
 * maps to text — the exact server message comes from the validation endpoint.
 */
export const classifyFlightArea = (geometry) => {
  if (!geometry) return { kind: FLIGHT_AREA_KIND.NONE };
  const type = geometry.type;
  if (type === 'Polygon') {
    const rings = geometry.coordinates;
    if (!Array.isArray(rings) || rings.length === 0 || !Array.isArray(rings[0]) || !rings[0].every(isPosition)) {
      return { kind: FLIGHT_AREA_KIND.INVALID, reason: 'malformed' };
    }
    if (rings.length > 1) return { kind: FLIGHT_AREA_KIND.INVALID, reason: 'holes' };
    return { kind: FLIGHT_AREA_KIND.POLYGON };
  }
  if (type === 'LineString') {
    const coords = geometry.coordinates;
    if (!Array.isArray(coords) || coords.length < 2 || !coords.every(isPosition)) {
      return { kind: FLIGHT_AREA_KIND.INVALID, reason: 'malformed' };
    }
    return { kind: FLIGHT_AREA_KIND.LINE };
  }
  if (type === 'Point') {
    return isPosition(geometry.coordinates)
      ? { kind: FLIGHT_AREA_KIND.CIRCLE }
      : { kind: FLIGHT_AREA_KIND.INVALID, reason: 'malformed' };
  }
  if (type === 'MultiPoint') return { kind: FLIGHT_AREA_KIND.INVALID, reason: 'points' };
  if (type === 'FeatureCollection' || type === 'Feature' || type === 'GeometryCollection'
      || type === 'MultiPolygon' || type === 'MultiLineString') {
    return { kind: FLIGHT_AREA_KIND.INVALID, reason: 'unsupported' };
  }
  return { kind: FLIGHT_AREA_KIND.INVALID, reason: 'unsupported' };
};

/** Vertices as {lat, lng}: a Polygon's outer ring without the closing duplicate, a LineString as-is
 * (none for a circle — see getCircleCenter). */
export const getVertices = (geometry) => {
  const kind = classifyFlightArea(geometry).kind;
  if (kind === FLIGHT_AREA_KIND.POLYGON) {
    const ring = geometry.coordinates[0];
    const last = ring[ring.length - 1];
    const closed = ring.length > 1 && ring[0][0] === last[0] && ring[0][1] === last[1];
    return (closed ? ring.slice(0, -1) : ring).map(([lng, lat]) => ({ lat, lng }));
  }
  if (kind === FLIGHT_AREA_KIND.LINE) {
    return geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));
  }
  return [];
};

/** The centre of a circle flight area as {lat, lng}, null for any other geometry. */
export const getCircleCenter = (geometry) => {
  if (classifyFlightArea(geometry).kind !== FLIGHT_AREA_KIND.CIRCLE) return null;
  const [lng, lat] = geometry.coordinates;
  return { lat, lng };
};

const METRES_PER_DEGREE_LAT = 111320;

/**
 * Every [lat, lng] the map should fit — any parseable position, valid or legacy-invalid geometry. A
 * circle with a radius adds the points one radius north, south, east and west of its centre.
 */
export const collectLatLngs = (geometry, circleRadiusM = null) => {
  const out = [];
  const center = getCircleCenter(geometry);
  const radius = parseCircleRadius(circleRadiusM);
  if (center && radius > 0) {
    const dLat = radius / METRES_PER_DEGREE_LAT;
    const dLng = radius / (METRES_PER_DEGREE_LAT * Math.max(Math.cos((center.lat * Math.PI) / 180), 0.01));
    out.push(
      [center.lat + dLat, center.lng], [center.lat - dLat, center.lng],
      [center.lat, center.lng + dLng], [center.lat, center.lng - dLng],
    );
  }
  const walk = (node) => {
    if (!Array.isArray(node)) return;
    if (isPosition(node)) { out.push([node[1], node[0]]); return; }
    node.forEach(walk);
  };
  const visit = (g) => {
    if (!g) return;
    if (g.type === 'FeatureCollection') (g.features || []).forEach((f) => visit(f?.geometry));
    else if (g.type === 'Feature') visit(g.geometry);
    else if (g.type === 'GeometryCollection') (g.geometries || []).forEach(visit);
    else walk(g.coordinates);
  };
  visit(geometry);
  return out;
};

/** Web Mercator ground resolution (m per CSS pixel) at a zoom level and latitude. */
export const metersPerPixel = (zoom, lat) =>
  (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);

/**
 * Stroke width (px) that draws a corridor of `widthM` total width around a line at this zoom — with
 * round caps and joins the stroke is exactly the line's buffer of widthM / 2 in map scale (Web Mercator
 * is conformal; the scale change across a corridor is negligible).
 */
export const corridorWeightPx = (widthM, zoom, lat) => {
  const mpp = metersPerPixel(zoom, lat);
  if (!(widthM > 0) || !(mpp > 0)) return 0;
  return widthM / mpp;
};

/** A metre value typed into a field (number or text, decimal comma allowed); null when empty or not a number. */
const parseMetres = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

export const parseCorridorWidth = parseMetres;
export const parseCircleRadius = parseMetres;

/**
 * Corridor width problem for a LineString against the server's range: 'required' | 'range' | null.
 * Without settings (not loaded yet) only "missing / not positive" is checked; the server decides.
 */
export const getCorridorWidthIssue = (widthM, settings) => {
  const w = parseCorridorWidth(widthM);
  if (w === null || !(w > 0)) return 'required';
  if (settings && (w < settings.corridor_width_min_m || w > settings.corridor_width_max_m)) return 'range';
  return null;
};

/** The circle radius range from the server settings as { min, default, max }; null before they load. */
export const circleRadiusLimitsFromSettings = (settings) => {
  if (!settings) return null;
  const { circle_radius_min_m: min, circle_radius_default_m: def, circle_radius_max_m: max } = settings;
  if (![min, def, max].every(Number.isFinite)) return null;
  return { min, default: def, max };
};

/**
 * Circle radius problem against the server's range ({ min, max }): 'required' | 'range' | null.
 * Without limits (settings not loaded yet) only "missing / not positive" is checked; the server decides.
 */
export const getCircleRadiusIssue = (radiusM, limits) => {
  const r = parseCircleRadius(radiusM);
  if (r === null || !(r > 0)) return 'required';
  if (limits && (r < limits.min || r > limits.max)) return 'range';
  return null;
};

/** A drawn or dragged radius in whole metres within the limits (only rounded without limits). */
export const clampCircleRadius = (radiusM, limits) => {
  const r = Math.round(radiusM);
  return limits ? Math.min(limits.max, Math.max(limits.min, r)) : r;
};

// A circle drawn by a click, or by a drag shorter than this, gets the default radius from the settings
// (a hand never holds the mouse perfectly still while clicking).
export const CIRCLE_CLICK_DRAG_THRESHOLD_PX = 6;

/**
 * The radius draft outside a circle's vertex-edit session. During the session the draft is
 * { active: true, value, origin }: `value` the raw radius (a number from the map, the text of a field,
 * null for an empty field), `origin` 'layer' (session start, dragging the radius handle) or 'input'
 * (the radius popup or the panel field) — only an 'input' draft is applied to the circle on the map.
 */
export const CIRCLE_DRAFT_IDLE = Object.freeze({ active: false, value: null, origin: null });

const EARTH_RADIUS_M = 6371008.8;

/** Great-circle distance (m) between two {lat, lng} points. */
const haversineM = (a, b) => {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
};

/** Whether the point (the take-off point) lies at the centre of a circle flight area, within `toleranceM`. */
export const isPointAtCircleCenter = (lat, lng, geometry, toleranceM = 1) => {
  const center = getCircleCenter(geometry);
  if (!center || !Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  return haversineM({ lat, lng }, center) <= toleranceM;
};

/**
 * Whether a STORED mission's take-off point / flight area needs fixing: no point, no or an unusable
 * area, a route without a valid corridor width, a circle without a valid radius, or anything the
 * server's validation of that stored data rejects (self-crossing polygon, take-off point too far or
 * outside the measurable range, width outside the configured range …). `validationResult` must be
 * the validation of the mission's stored values, never of an edited draft — otherwise the
 * completed-mission lock below could be bypassed by editing. Used by the edit dialog (lock exception)
 * and the mission detail ("Edit mission" prompt).
 */
export const storedFlightAreaNeedsFix = ({ mission, settings = null, validationResult = null }) => {
  if (!mission) return false;
  if (!Number.isFinite(mission.latitude) || !Number.isFinite(mission.longitude)) return true;
  const kind = classifyFlightArea(mission.flight_area).kind;
  if (kind === FLIGHT_AREA_KIND.NONE || kind === FLIGHT_AREA_KIND.INVALID) return true;
  if (kind === FLIGHT_AREA_KIND.LINE && getCorridorWidthIssue(mission.corridor_width_m, settings) !== null) return true;
  if (kind === FLIGHT_AREA_KIND.CIRCLE
    && getCircleRadiusIssue(mission.circle_radius_m, circleRadiusLimitsFromSettings(settings)) !== null) return true;
  return validationResult?.valid === false;
};

/**
 * A completed mission's area and take-off point are locked — unless its stored state needs fixing
 * (see storedFlightAreaNeedsFix), so a legacy mission can still be completed and saved.
 */
export const isFlightAreaLocked = ({ mission, settings = null, validationResult = null }) =>
  mission?.lifecycle_status === 'completed' && !storedFlightAreaNeedsFix({ mission, settings, validationResult });

// The click that finishes a drawing (closing a polygon, finishing a line) reaches the map as a click
// only after leaflet-draw has already ended the session — clicks this soon after a session ends are
// swallowed, so that click never moves the take-off point onto the last vertex.
export const CLICK_GUARD_AFTER_SESSION_MS = 400;

/** Whether a map click must not place the take-off point: a draw/edit/delete session is open or has
 * just ended. `session` = { active, endedAt } kept synchronously from leaflet-draw's events. */
export const isMapClickSuppressed = (session, now = Date.now()) =>
  !!session && (session.active || now - (session.endedAt || 0) < CLICK_GUARD_AFTER_SESSION_MS);

/**
 * Why the mission dialog's main save is blocked: 'map_session' while a draw / edit / delete session
 * is open on the map (its geometry is not in the form yet — saving would store the old one),
 * 'flight_area' when take-off point / area blockers remain, otherwise null.
 */
export const missionSaveBlockReason = ({ mapSessionActive = false, flightAreaBlockers = [] }) => {
  if (mapSessionActive) return 'map_session';
  if (flightAreaBlockers.length > 0) return 'flight_area';
  return null;
};

// Server issue codes (AppErrorCodes) that mean "not filled in yet" rather than "filled in but invalid".
const MISSING_ISSUE_CODES = [
  'flight_area_required', 'takeoff_point_required', 'corridor_width_required', 'circle_radius_required',
];

/** Whether a server validation issue reports a missing value (vs. an invalid one). */
export const isMissingIssue = (issue) => MISSING_ISSUE_CODES.includes(issue?.code);

/**
 * The save-blocker texts for the dialog footer and the save toast: what is still missing
 * ("Missing before saving: …") apart from what is filled in but invalid ("Cannot save — …", e.g. a
 * self-crossing polygon or a take-off point too far away), which is never reported as "missing".
 */
export const flightAreaBlockerMessages = ({ missing = [], invalid = [] }, translate) => {
  const messages = [];
  if (missing.length > 0) messages.push(translate('flightArea.missingForSave', { items: missing.join('; ') }));
  if (invalid.length > 0) messages.push(translate('flightArea.invalidForSave', { items: invalid.join('; ') }));
  return messages;
};

/**
 * The flight-area part of a POST/PUT /missions payload. corridor_width_m is sent only for a
 * LineString and circle_radius_m only for a circle, otherwise null (the server would ignore them
 * anyway); flight_area_source only when the geometry came from a file.
 */
export const buildFlightAreaPayload = ({ flight_area, corridor_width_m, circle_radius_m, flight_area_source }) => {
  const kind = classifyFlightArea(flight_area).kind;
  const payload = {
    flight_area: flight_area || null,
    corridor_width_m: kind === FLIGHT_AREA_KIND.LINE ? parseCorridorWidth(corridor_width_m) : null,
    circle_radius_m: kind === FLIGHT_AREA_KIND.CIRCLE ? parseCircleRadius(circle_radius_m) : null,
  };
  if (flight_area && flight_area_source) payload.flight_area_source = flight_area_source;
  return payload;
};

/**
 * Picks the one usable flight area out of an import preview (a FeatureCollection, a Feature or a bare
 * geometry). Selecting one of several features is deferred (OI-077), so anything but exactly one
 * Polygon without holes or one LineString is rejected — never silently the first feature.
 * A single-part MultiPolygon/MultiLineString (common in KML MultiGeometry) is unwrapped.
 * Returns { geometry } or { error: 'empty'|'multiple'|'points'|'holes'|'unsupported', count }.
 */
export const pickSingleFlightArea = (geojson) => {
  if (!geojson) return { error: 'empty', count: 0 };
  let geometries;
  if (geojson.type === 'FeatureCollection') {
    geometries = (geojson.features || []).map((f) => f?.geometry).filter(Boolean);
  } else if (geojson.type === 'Feature') {
    geometries = geojson.geometry ? [geojson.geometry] : [];
  } else {
    geometries = [geojson];
  }
  if (geometries.length === 0) return { error: 'empty', count: 0 };
  if (geometries.every((g) => g.type === 'Point' || g.type === 'MultiPoint')) {
    return { error: 'points', count: geometries.length };
  }
  if (geometries.length > 1) return { error: 'multiple', count: geometries.length };

  let geometry = geometries[0];
  if ((geometry.type === 'MultiPolygon' || geometry.type === 'MultiLineString')
      && Array.isArray(geometry.coordinates) && geometry.coordinates.length === 1) {
    geometry = { type: geometry.type === 'MultiPolygon' ? 'Polygon' : 'LineString', coordinates: geometry.coordinates[0] };
  } else if (geometry.type === 'MultiPolygon' || geometry.type === 'MultiLineString') {
    return { error: 'multiple', count: geometry.coordinates?.length || 0 };
  }
  const classified = classifyFlightArea(geometry);
  if (classified.kind === FLIGHT_AREA_KIND.POLYGON || classified.kind === FLIGHT_AREA_KIND.LINE) {
    return { geometry };
  }
  if (classified.reason === 'holes') return { error: 'holes', count: 1 };
  if (classified.reason === 'points') return { error: 'points', count: 1 };
  return { error: 'unsupported', count: 1 };
};

/** flight_area_source for an imported file, or null when the preview's format is not one the server accepts. */
export const toFlightAreaSource = (sourceFormat, originalFilename) => {
  const raw = String(sourceFormat || '').toLowerCase();
  const format = raw.startsWith('gpx') ? 'gpx' : raw;
  if (!FLIGHT_AREA_SOURCE_FORMATS.includes(format)) return null;
  return { format, original_filename: originalFilename || null };
};

export const formatMeters = (m, locale) => {
  if (m === null || m === undefined || !Number.isFinite(m)) return '—';
  if (m >= 1000) return `${(m / 1000).toLocaleString(locale, { maximumFractionDigits: 2 })} km`;
  return `${Math.round(m).toLocaleString(locale)} m`;
};

export const formatArea = (m2, locale) => {
  if (m2 === null || m2 === undefined || !Number.isFinite(m2)) return '—';
  if (m2 >= 1000000) return `${(m2 / 1000000).toLocaleString(locale, { maximumFractionDigits: 3 })} km²`;
  if (m2 >= 10000) return `${(m2 / 10000).toLocaleString(locale, { maximumFractionDigits: 2 })} ha`;
  return `${Math.round(m2).toLocaleString(locale)} m²`;
};

// ── Server settings (GET /missions/flight-area/settings) ─────────────────────────────────────────
// Loaded once per page load; a failed request is retried on the next mount instead of being cached.
let settingsPromise = null;

export const loadFlightAreaSettings = () => {
  if (!settingsPromise) {
    settingsPromise = api.get('/missions/flight-area/settings')
      .then((res) => res.data)
      .catch((err) => { settingsPromise = null; throw err; });
  }
  return settingsPromise;
};

export const useFlightAreaSettings = () => {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    let cancelled = false;
    loadFlightAreaSettings()
      .then((data) => { if (!cancelled) setSettings(data); })
      .catch((err) => console.warn('Failed to load flight area settings:', err));
    return () => { cancelled = true; };
  }, []);
  return settings;
};

// ── Live validation (POST /missions/flight-area/validate) ────────────────────────────────────────

export const validationInputKey = ({ flightArea, corridorWidthM, circleRadiusM, latitude, longitude }) =>
  JSON.stringify([
    flightArea || null, parseCorridorWidth(corridorWidthM), parseCircleRadius(circleRadiusM),
    latitude ?? null, longitude ?? null,
  ]);

/**
 * Debounced validation of the current area + width / radius + take-off point. Returns
 * { result, key, loading, failed }: `result` belongs to the inputs whose key is `key` — a caller must
 * not treat it as current when the inputs have changed since (validationInputKey differs). Without an
 * area nothing is sent (result null). A network/server failure leaves `failed` true and the save to
 * the server, which validates again anyway.
 */
export const useFlightAreaValidation = ({
  flightArea, corridorWidthM, circleRadiusM, latitude, longitude, enabled = true, delayMs = 400,
}) => {
  const [state, setState] = useState({ result: null, key: null, loading: false, failed: false });
  const seq = useRef(0);
  const inputs = { flightArea, corridorWidthM, circleRadiusM, latitude, longitude };
  const key = validationInputKey(inputs);

  useEffect(() => {
    const current = ++seq.current;
    if (!enabled || !flightArea) {
      setState({ result: null, key, loading: false, failed: false });
      return undefined;
    }
    setState((prev) => ({ ...prev, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const res = await api.post('/missions/flight-area/validate', {
          flight_area: flightArea,
          corridor_width_m: parseCorridorWidth(corridorWidthM),
          circle_radius_m: parseCircleRadius(circleRadiusM),
          latitude: latitude ?? null,
          longitude: longitude ?? null,
        });
        if (current === seq.current) setState({ result: res.data, key, loading: false, failed: false });
      } catch (err) {
        if (current === seq.current) setState({ result: null, key, loading: false, failed: true });
      }
    }, delayMs);
    return () => clearTimeout(timer);
    // `key` captures every input by value — flightArea is an object whose identity may change on each
    // render of the parent without its content changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, delayMs]);

  const isCurrent = state.key === key;
  return {
    result: isCurrent ? state.result : null,
    loading: state.loading || !isCurrent,
    failed: isCurrent && state.failed,
  };
};

/** The localized text of a server issue/error ({ message_cs, message_en, ... }). */
export const localizedIssue = (issue, language) =>
  (language === 'cs' ? issue?.message_cs : issue?.message_en) || issue?.message_en || issue?.message_cs || issue?.code || '';
